/**
 * Domain ownership verification panel.
 *
 * Shown in project settings for endpoint/hybrid projects. Lets the user prove
 * control of the target domain via a DNS TXT record OR an HTML meta tag, then
 * runs the check. Until verified, the project cannot start scans or agents.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert, Copy, Check, Loader2, Globe, FileCode } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

type Method = 'dns' | 'meta';

interface VerificationData {
  applicable: boolean;
  domain?: string;
  status?: 'pending' | 'verified';
  verified?: boolean;
  verifiedAt?: string | null;
  verifiedMethod?: string | null;
  lastError?: string | null;
  token?: string;
  methods?: {
    dns: { label: string; recordType: string; recordName: string; recordValue: string };
    meta: { label: string; tag: string };
  };
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="shrink-0 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
      aria-label="Copy"
    >
      {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2 bg-muted/50 border border-border rounded-lg px-3 py-2">
        <code className="text-xs text-foreground break-all flex-1 font-mono">{value}</code>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

export function DomainVerificationPanel({
  orgId, portfolioId, projectId,
}: { orgId: string; portfolioId: string; projectId: string }) {
  const qc = useQueryClient();
  const [method, setMethod] = useState<Method>('dns');

  const { data, isLoading } = useQuery({
    queryKey: ['domain-verification', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getDomainVerification(orgId, portfolioId, projectId),
  });
  const v = (data?.data as VerificationData | undefined);

  const check = useMutation({
    mutationFn: () => apiClient.checkDomainVerification(orgId, portfolioId, projectId, method),
    onSuccess: (res) => {
      const d = res?.data as VerificationData & { justVerified?: boolean; error?: string | null };
      if (d?.verified) {
        toast.success('Domain verified — scans and agents are now unlocked.');
      } else {
        toast.error(d?.error || 'Verification failed. Double-check the record and try again.');
      }
      qc.invalidateQueries({ queryKey: ['domain-verification', orgId, portfolioId, projectId] });
      qc.invalidateQueries({ queryKey: ['project', orgId, portfolioId, projectId] });
    },
    onError: (e: Error) => toast.error(e.message || 'Verification check failed.'),
  });

  if (isLoading) {
    return (
      <Card className="p-6 flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading domain verification…
      </Card>
    );
  }
  // Repo projects have no domain to verify.
  if (!v || v.applicable === false) return null;

  if (v.verified) {
    return (
      <Card className="p-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">Domain verified</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Ownership of <span className="font-mono text-foreground">{v.domain}</span> is verified
              {v.verifiedMethod ? ` via ${v.verifiedMethod === 'dns' ? 'DNS TXT record' : 'HTML meta tag'}` : ''}
              {v.verifiedAt ? ` on ${new Date(v.verifiedAt).toLocaleDateString()}` : ''}. Scans and agents are unlocked.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const dns = v.methods?.dns;
  const meta = v.methods?.meta;

  return (
    <Card className="p-6 border-amber-500/30">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
          <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-foreground">Verify domain ownership</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Before scanning <span className="font-mono text-foreground">{v.domain}</span>, prove you control it.
            Scans and agents are blocked until this is verified. Choose one method:
          </p>
        </div>
      </div>

      {/* Method toggle */}
      <div className="flex gap-2 mb-4">
        <button
          type="button"
          onClick={() => setMethod('dns')}
          className={cn('flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
            method === 'dns' ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/40')}
        >
          <Globe className="h-4 w-4" /> DNS TXT record
        </button>
        <button
          type="button"
          onClick={() => setMethod('meta')}
          className={cn('flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
            method === 'meta' ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border text-muted-foreground hover:bg-muted/40')}
        >
          <FileCode className="h-4 w-4" /> HTML meta tag
        </button>
      </div>

      {/* Instructions */}
      {method === 'dns' && dns && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Add this <span className="font-medium text-foreground">TXT</span> record to your DNS, then click Verify.
            DNS can take a few minutes to propagate.
          </p>
          <Field label="Record type" value={dns.recordType} />
          <Field label="Name / Host" value={dns.recordName} />
          <Field label="Value" value={dns.recordValue} />
        </div>
      )}
      {method === 'meta' && meta && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Add this tag inside the <span className="font-mono text-foreground">&lt;head&gt;</span> of
            <span className="font-mono text-foreground"> https://{v.domain}/</span>, then click Verify.
          </p>
          <Field label="Meta tag" value={meta.tag} />
        </div>
      )}

      {v.lastError && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-3">{v.lastError}</p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => check.mutate()} disabled={check.isPending}>
          {check.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying…</> : 'Verify domain'}
        </Button>
        <span className="text-xs text-muted-foreground">Checks the {method === 'dns' ? 'DNS record' : 'homepage meta tag'} now.</span>
      </div>
    </Card>
  );
}
